import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { AuthLayout } from '../../../../components/AuthLayout';
import { AuthFormHeader } from '../../../../components/AuthFormHeader';
import { Button } from '../../../../components/Button';
import { InputText } from '../../../../components/InputText';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import { formatDateForServer } from '../../../../utils/formatDate';
import { getValidationErrors } from '../../../../utils/getValidationErrors';
import { phoneMask } from '../../../../utils/inputMasks';

import {
  Checkbox,
  ContainerCheckboxBox,
  EmailTag,
  Form,
  Row,
  Row2,
  Separate,
} from './styles';

interface locationState {
  fullname?: string;
  nickname?: string;
  email?: string;
  gym_id?: string;
  code?: string;
}

interface formProps {
  fullname: string;
  nickname: string;
  birthday?: string;
  phone?: string;
  password: string;
  password_confirmation: string;
  min_age: boolean;
  privacity: boolean;
  terms_accepted: boolean;
}

const SignUpIdentity: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { addToast } = useToast();
  const [enableDisableSubmit, setenableDisableSubmit] = useState(false);

  const { fullname, nickname, email, gym_id, code } =
    (state as locationState) || {};

  useEffect(() => {
    if (!code || !email) {
      navigate('/sign-up', { replace: true });
    }
  }, [code, email, navigate]);

  const handleEnableOrDisableSubmit = useCallback(() => {
    setenableDisableSubmit(
      formRef.current?.getFieldValue('min_age') &&
        formRef.current?.getFieldValue('privacity') &&
        formRef.current?.getFieldValue('terms_accepted'),
    );
  }, []);

  const handleSubmit = useCallback(
    async (data: formProps) => {
      try {
        formRef.current?.setErrors({});

        const schema = yup.object().shape({
          fullname: yup.string().required('Campo obrigatório'),
          nickname: yup.string().required('Campo obrigatório'),
          phone: yup
            .string()
            .nullable()
            .transform((value: string, o: string) => (o === '' ? null : value))
            .min(10, 'Insira o seu telefone com DDD'),
          birthday: yup.string(),
          password: yup
            .string()
            .min(8, 'A senha deve ter no mínimo 8 caracteres'),
          password_confirmation: yup
            .string()
            .min(8, 'A senha deve ter no mínimo 8 caracteres')
            .oneOf([yup.ref('password'), null], 'Senhas não coincidem.'),
          min_age: yup.boolean().required().isTrue(),
          privacity: yup.boolean().required().isTrue(),
          terms_accepted: yup.boolean().required().isTrue(),
        });

        await schema.validate(data, { abortEarly: false });

        await api.post(
          '/users',
          {
            fullname: data.fullname,
            nickname: data.nickname,
            phone: phoneMask(data?.phone, true) || undefined,
            birthday: formatDateForServer(data.birthday) || undefined,
            email,
            password: data.password,
            minAge: data.min_age,
            privacity: data.privacity,
            termsAccepted: data.terms_accepted,
          },
          {
            headers: {
              code: (code || '').toUpperCase(),
            },
          },
        );

        addToast({
          type: 'success',
          title: 'Cadastro finalizado com sucesso.',
          description:
            'Faça o login e curta a nossa plataforma, bons treinos! 😁',
        });

        navigate('/sign-in');
      } catch (err: any) {
        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização dos dados',
          description:
            'Houve um erro na atualização dos dados. Tente novamente mais tarde.',
        });
      }
    },
    [addToast, code, email, navigate],
  );

  return (
    <AuthLayout
      eyebrow="Fluxo de cadastro"
      title={
        <>
          COMPLETE SEU <span>PERFIL</span>
        </>
      }
      subtitle="Seu perfil é a base do seu desempenho. Preencha os dados abaixo para começar a treinar."
    >
      <AuthFormHeader
        badge="Passo 2 de 2 · Identidade"
        title="Dados de cadastro"
        subtitle="Complete o seu cadastro inserindo as informações solicitadas abaixo."
      />

      {!!email && (
        <EmailTag>
          <span>{email}</span>
        </EmailTag>
      )}

      <Form ref={formRef} onSubmit={handleSubmit}>
        <Row>
          <InputText
            name="fullname"
            label="Nome completo"
            defaultValue={fullname}
          />
          <InputText name="nickname" label="Apelido" defaultValue={nickname} />
        </Row>

        <Row2>
          <InputText name="birthday" label="Data de nascimento" type="date" />
          <InputText
            name="phone"
            label="Telefone"
            type="tel"
            maxLength={11}
            onBlur={() => {
              formRef.current?.setFieldValue(
                'phone',
                phoneMask(formRef.current?.getFieldValue('phone')),
              );
            }}
            onChange={() => {
              formRef.current?.setFieldValue(
                'phone',
                formRef.current?.getFieldValue('phone').replace(/\D/g, ''),
              );
            }}
          />
        </Row2>

        <Row2>
          <InputText name="password" label="Senha" type="password" />
          <InputText
            name="password_confirmation"
            label="Confirme a senha"
            type="password"
          />
        </Row2>

        <Separate label="Consentimento" />

        <ContainerCheckboxBox>
          <Checkbox
            name="min_age"
            id="min_age"
            onChange={handleEnableOrDisableSubmit}
          />
          <label htmlFor="min_age">Tenho mais de 18 anos de idade.</label>
        </ContainerCheckboxBox>

        <ContainerCheckboxBox>
          <Checkbox
            name="privacity"
            id="privacity"
            onChange={handleEnableOrDisableSubmit}
          />
          <label htmlFor="privacity">
            Li e aceito as{' '}
            <a
              href={`${process.env.REACT_APP_SITE_URL}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              políticas de privacidade
            </a>{' '}
            da plataforma TRENZOX.
          </label>
        </ContainerCheckboxBox>

        <ContainerCheckboxBox>
          <Checkbox
            name="terms_accepted"
            id="terms_accepted"
            onChange={handleEnableOrDisableSubmit}
          />
          <label htmlFor="terms_accepted">
            Li e aceito os{' '}
            <a
              href={`${process.env.REACT_APP_SITE_URL}/terms`}
              target="_blank"
              rel="noopener noreferrer"
            >
              termos de uso
            </a>{' '}
            da plataforma TRENZOX.
          </label>
        </ContainerCheckboxBox>

        <Button
          name="submit"
          value="Finalizar cadastro"
          disabled={!enableDisableSubmit}
        />
      </Form>
    </AuthLayout>
  );
};

export { SignUpIdentity };
